import { useState, useRef } from 'react';
import { Modal, ModalBody, ModalFooter, Alert, Spinner } from '@components/ui';
import { CTA, GHOST } from '@components/ui/designTokens';
import { ArrowDownTrayIcon, ArrowUpTrayIcon, DocumentIcon, XMarkIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import { customerService } from '../services/customerService';

interface CustomerImportError {
  row: number;
  field: string;
  message: string;
  value?: string;
}

interface CustomerImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CustomerImportModal({ isOpen, onClose, onSuccess }: CustomerImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [errors, setErrors] = useState<CustomerImportError[]>([]);
  const [generalError, setGeneralError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      await customerService.downloadTemplate();
    } catch (error) {
      setGeneralError('Error al descargar la plantilla');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setErrors([]);
      setGeneralError('');
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setIsLoading(true);
    setErrors([]);
    setGeneralError('');

    try {
      const result = await customerService.importCustomers(file);

      // El backend puede responder exito PARCIAL: unas filas entraron y otras no. Antes
      // ese caso era indistinguible de un exito total porque el detalle de fallos solo se
      // leia en el catch, y una respuesta 2xx no pasa por ahi — el usuario cerraba el
      // modal creyendo que se importo todo. Ahora el listado se refresca (lo que si entro
      // ya esta creado) pero el modal queda abierto mostrando que filas fallaron.
      if (result.failed && result.failed > 0) {
        onSuccess();
        setErrors(result.errors ?? []);
        setGeneralError(
          `Se importaron ${result.imported} clientes. ${result.failed} fila(s) no se pudieron importar:`
        );
        return;
      }

      if (result.success) {
        onSuccess();
        handleClose();
      }
    } catch (error: any) {
      console.error('Import error:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setGeneralError(error.response?.data?.message || 'Error al importar clientes');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setErrors([]);
    setGeneralError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Importar Clientes desde Excel"
      kicker="Carga masiva"
      description="Descarga la plantilla, completa los datos y súbela. Se validan y reportan los errores por fila antes de importar."
      icon={<TableCellsIcon />}
      size="2xl"
    >
      <ModalBody className="h-auto max-h-[70vh] overflow-y-auto">
        <div className="space-y-5">
          {/* ===== 01 · Plantilla ===== */}
          <div className="rounded-2xl border border-[#eef0f2] bg-[#fbfbfc] p-5 dark:border-secondary-700 dark:bg-secondary-900">
            <div className="flex items-start gap-4">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-500/10 font-mono text-[12px] font-bold text-[#0d9488]">
                01
              </span>
              <div className="min-w-0 flex-1">
                <h4 className="text-[14px] font-bold text-ink-850 dark:text-white">
                  Descargar plantilla
                </h4>
                <p className="mt-1 text-[13px] leading-[1.5] text-secondary-500 dark:text-secondary-400">
                  Descarga la plantilla Excel y completa los datos de tus clientes. La plantilla incluye instrucciones y ejemplos.
                </p>
                <button
                  type="button"
                  className={`${GHOST} mt-3.5`}
                  onClick={handleDownloadTemplate}
                  disabled={isDownloading}
                  data-testid="customer-import-modal-download-template-button"
                >
                  {isDownloading ? <Spinner size="sm" /> : <ArrowDownTrayIcon className="h-3.5 w-3.5" />}
                  {isDownloading ? 'Descargando...' : 'Descargar Plantilla'}
                </button>
              </div>
            </div>
          </div>

          {/* ===== 02 · Archivo ===== */}
          <div className="rounded-2xl border border-[#eef0f2] p-5 dark:border-secondary-700">
            <div className="flex items-start gap-4">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#6b239e]/10 font-mono text-[12px] font-bold text-[#6b239e]">
                02
              </span>
              <div className="min-w-0 flex-1">
                <h4 className="text-[14px] font-bold text-ink-850 dark:text-white">
                  Subir archivo
                </h4>
                <p className="mt-1 text-[13px] leading-[1.5] text-secondary-500 dark:text-secondary-400">
                  Selecciona el archivo Excel con tus clientes para importar.
                </p>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".xlsx,.xls"
                  className="hidden"
                 data-testid="customer-import-modal-file-input"/>

                {!file ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-3.5 flex w-full flex-col items-center gap-2 rounded-[14px] border-[1.5px] border-dashed border-secondary-300 bg-secondary-50 px-5 py-8 text-center transition-colors hover:border-teal-500 hover:bg-teal-500/[.03] dark:border-secondary-600 dark:bg-secondary-900 dark:hover:border-teal-400" data-testid="customer-import-modal-select-file-button"
                  >
                    <ArrowUpTrayIcon className="h-7 w-7 text-secondary-400" />
                    <span className="text-[14px] font-semibold text-ink-850 dark:text-white">
                      Haz clic para seleccionar archivo Excel
                    </span>
                    <span className="font-mono text-[12px] uppercase tracking-[0.14em] text-secondary-400">
                      .xlsx · .xls · max 5 MB
                    </span>
                  </button>
                ) : (
                  <div className="mt-3.5 flex items-center gap-3 rounded-xl border border-[#eef0f2] bg-white p-3.5 dark:border-secondary-700 dark:bg-secondary-800">
                    <DocumentIcon className="h-8 w-8 shrink-0 text-[#0d9488]" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[14px] font-semibold text-ink-850 dark:text-white">
                        {file.name}
                      </p>
                      <p className="font-mono text-[12px] text-secondary-400">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveFile}
                      aria-label="Quitar archivo"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-secondary-400 hover:border-[#ececec] hover:bg-[#f6f6f7] hover:text-ink-850 dark:hover:border-secondary-600 dark:hover:bg-secondary-700" data-testid="customer-import-modal-remove-file-button"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* General Error */}
          {generalError && (
            <Alert variant="danger" title="Error" data-testid="customer-import-modal-general-error-alert">
              {generalError}
            </Alert>
          )}

          {/* Validation Errors */}
          {errors.length > 0 && (
            <Alert variant="danger" title="Errores de validacion" data-testid="customer-import-modal-validation-errors-alert">
              <p className="mb-2 text-[14px]">Se encontraron {errors.length} errores en el archivo:</p>
              <div className="max-h-40 overflow-y-auto rounded-lg border border-danger-200 bg-danger-50/50 p-2.5 dark:border-danger-800 dark:bg-danger-900/20">
                <ul className="list-inside list-disc space-y-1">
                  {errors.map((error, index) => (
                    <li key={index} className="text-[13px]">
                      <span className="font-semibold">Fila {error.row}</span>, {error.field}: {error.message}
                      {error.value && (
                        <span className="text-secondary-500 dark:text-secondary-400"> (valor: "{error.value}")</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </Alert>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <button type="button" onClick={handleClose} className={GHOST} data-testid="customer-import-modal-cancel-button">
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleImport}
          disabled={!file || isLoading}
          className={CTA} data-testid="customer-import-modal-import-button"
        >
          {isLoading ? (
            <>
              <Spinner size="sm" />
              <span>Importando...</span>
            </>
          ) : (
            <>
              <ArrowUpTrayIcon className="h-3.5 w-3.5" />
              <span>Importar Clientes</span>
            </>
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
}
